import { isEqual, uniqBy } from "lodash";
import { CalendarMode, CalendarView, Row } from "PFApp/booking/types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Filters } from "PFTypes";
import { useEffect, useRef } from "react";

import { getRowFromDataItem } from "../../../helpers";
import { ProjectsCalendarData } from "../booking_overview_context.types";
import { useBookingsOverviewContext } from "../bookings_overview_context";
import { BookingRowsContextType } from "./booking_rows_context";
import { enhancePrevRows } from "./use_rows.utils";

interface UseProjectsRows {
  setRowsByCalendarMode: BookingRowsContextType["setRowsByCalendarMode"];
  onRowsReplace: VoidFunction;
}

export const useProjectsRows = ({ setRowsByCalendarMode, onRowsReplace }: UseProjectsRows): void => {
  const { data: currentProfile } = useCurrentProfile();

  const prevFilters = useRef<{ metaFilters?: Filters; childrenFiltersEnabled: boolean }>();
  const prevSortOrder = useRef<string>();
  const prevView = useRef<CalendarView>();
  const prevPage = useRef<number>();

  const {
    calendarMode,
    calendarData: { data, view, page, childrenFiltersEnabled }
  } = useBookingsOverviewContext<ProjectsCalendarData>();

  useEffect(() => {
    if (calendarMode !== CalendarMode.Projects) {
      return;
    }
    const currentFiltersState = { metaFilters: data.meta?.filters, childrenFiltersEnabled };
    const isFilterChanged = !isEqual(prevFilters.current, currentFiltersState);
    const isOrderChanged = !isEqual(prevSortOrder.current, data.meta.order?.value?.text);
    const isViewChanged = !isEqual(prevView.current, view);
    const isPageChanged = !isEqual(prevPage.current, page);

    const shouldReplaceRows = isFilterChanged || isOrderChanged || isViewChanged || isPageChanged;
    setRowsByCalendarMode((prevState) => {
      const prevRows = prevState[calendarMode];
      const newRows: Row[] = data.entries.map((row) =>
        getRowFromDataItem(row, currentProfile, CalendarMode.Projects)
      );
      const rows = shouldReplaceRows
        ? newRows
        : uniqBy(enhancePrevRows(prevRows, newRows).concat(newRows), (row) => row.key);
      return {
        ...prevState,
        [calendarMode]: rows
      };
    });

    if (shouldReplaceRows) {
      onRowsReplace();
    }

    prevFilters.current = currentFiltersState;
    prevSortOrder.current = data.meta.order?.value?.text;
    prevView.current = view;
    prevPage.current = page;
  }, [data.entries]);
};
